// src/context/AuthContext.js
import { createContext, useState, useEffect, useCallback } from "react";
import { getUserPermissions, getUserProfile } from "../services/api";
import { notify } from "../utils/notify"; // استخدام notify بدل alert
import { logout as apiLogout } from "../services/api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);

  // The signed-in user's own profile (avatar, name, groups, ...) — kept
  // here instead of fetched separately inside MainLayout's ProfileMenu so
  // that anything able to change it (Profile.js saving a new photo, e.g.)
  // can push the fresh copy through setUserProfile and have every consumer
  // — the header avatar included — re-render immediately, with no page
  // reload. Seeded from the last-known cache so the header doesn't flash to
  // the default avatar on first paint, before the fetch below resolves.
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const cached = localStorage.getItem("userProfile");
      if (cached && cached !== "undefined" && cached !== "null") {
        return JSON.parse(cached);
      }
    } catch (e) {
      console.error("Cached profile is corrupted", e);
    }
    return null;
  });

  // مؤقت inactivity
  let inactivityTimer;
  let warningTimer;

  /* const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUserPermissions([]);
    notify.info("You have been logged out due to inactivity ⏰");
  }, []); */

  const logout = useCallback(async () => {
    try {
        const currentToken = localStorage.getItem("token");
        if (currentToken) {
            // 1. نرسل الطلب للباك إند أولاً
            await apiLogout(currentToken);
        }
    } catch (error) {
        console.error("Logout request failed:", error);
    } finally {
        // 2. نقوم بمسح البيانات محلياً في كل الأحوال
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        setToken(null);
        setUserPermissions([]);
        setUserProfile(null);

        // 3. التوجيه (إذا لم يكن تلقائياً من خلال الـ State)
        // window.location.href = "/login";

        notify.info("You have been logged out ⏰");
    }
}, [setToken, setUserPermissions]); // تأكد من إضافة التبعيات (dependencies)

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer);
    clearTimeout(warningTimer);

    // قراءة الاعدادات من localStorage (بعد الحفظ في ConfigPage)
    //const config = JSON.parse(localStorage.getItem("systemConfig") || "{}");
    let config = {};
    try {
      const savedConfig = localStorage.getItem("systemConfig");
      if (savedConfig && savedConfig !== "undefined" && savedConfig !== "null") {
        config = JSON.parse(savedConfig);
      }
    } catch (e) {
      console.error("Error parsing systemConfig", e);
    }
    const timeoutMinutes = config.session?.inactivity_timeout || 15;
    const warningMinutes = config.session?.auto_logout_warning || 5;

    // التحذير قبل انتهاء الوقت
/*     warningTimer = setTimeout(() => {
      notify.warning(
        `Your session will expire in ${warningMinutes} minute(s)! ⚠️`,
        { 
          autoClose: (timeoutMinutes - warningMinutes) * 60 * 1000, 
          showCountdown: true,       // ✅ نعرض العد التنازلي في الدائرة
          closeOnUserActivity: true, // ✅ يغلق تلقائيًا إذا المستخدم تحرك
          theme: "light",            // "dark" أو "light"
        }
      );
    }, (timeoutMinutes - warningMinutes) * 60 * 1000); */

    warningTimer = setTimeout(() => {
      notify.warning(`Your session will expire in ${warningMinutes} minute(s)! ⚠️`, {
        autoClose: warningMinutes * 60 * 1000,
        showCountdown: true,
        closeOnUserActivity: true
      });
    }, (timeoutMinutes - warningMinutes) * 60 * 1000);
    

    // تسجيل الخروج بعد انتهاء الوقت
    inactivityTimer = setTimeout(() => {
      logout();
    }, timeoutMinutes * 60 * 1000);
  }, [logout]);

  // 🧠 مراقبة نشاط المستخدم
  useEffect(() => {
    if (!token) return;

    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((event) => window.addEventListener(event, resetInactivityTimer));

    resetInactivityTimer(); // ابدأ المؤقت أول مرة

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetInactivityTimer));
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
    };
  }, [token, resetInactivityTimer]);

  // تحميل الصلاحيات كالعادة
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!token) {
        setUserPermissions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const perms = await getUserPermissions(token);
        setUserPermissions(perms);
      } catch (err) {
        console.warn("Failed to fetch permissions:", err.message);
        logout();
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [token, logout]);

  // نفس فكرة تحميل الصلاحيات فوق، لكن لبيانات البروفايل (الصورة، الاسم...)
  // — كانت قبل كده بتتحمّل بشكل منفصل جوه ProfileMenu في MainLayout، فأي
  // تحديث من صفحة البروفايل (زي رفع صورة جديدة أو استخدام Reset Photo) كان
  // مايوصلش للهيدر غير بعد إعادة تحميل الصفحة بالكامل.
  useEffect(() => {
    if (!token) {
      setUserProfile(null);
      localStorage.removeItem("userProfile");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await getUserProfile(token);
        if (!cancelled && data) {
          setUserProfile(data);
          localStorage.setItem("userProfile", JSON.stringify(data));
        }
      } catch (err) {
        console.warn("Failed to fetch profile from API, keeping cached copy if any:", err.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = (tokenValue) => {
    localStorage.setItem("token", tokenValue);
    setToken(tokenValue);
    resetInactivityTimer(); // ابدأ المؤقت بعد تسجيل الدخول مباشرة
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        userPermissions,
        loading,
        login,
        logout,
        redirectAfterLogin,
        setRedirectAfterLogin,
        userProfile,
        setUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
